import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Stars, OrbitControls, Line, Billboard, Html } from '@react-three/drei';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useCallback, useMemo, useId } from 'react';
import * as THREE from 'three';

// src/realms/cosmos/components/World.tsx

// src/themes/index.ts
var THEME_TOKENS = {
  cosmos: {
    bgDeep: "#04070d"}};
function World({ children, dpr = [1, 2] }) {
  return /* @__PURE__ */ jsxs(
    Canvas,
    {
      camera: { position: [0, 6, 32], fov: 60, near: 0.1, far: 500 },
      dpr,
      gl: { antialias: true },
      children: [
        /* @__PURE__ */ jsx("color", { attach: "background", args: [THEME_TOKENS.cosmos.bgDeep] }),
        /* @__PURE__ */ jsx("ambientLight", { intensity: 0.15 }),
        /* @__PURE__ */ jsx(
          Stars,
          {
            radius: 120,
            depth: 60,
            count: 3e3,
            factor: 4,
            fade: true,
            saturation: 0.4
          }
        ),
        /* @__PURE__ */ jsx(OrbitControls, { enablePan: false, maxDistance: 80, minDistance: 4 }),
        children
      ]
    }
  );
}
function Star({
  visual,
  onSelect,
  dimmed,
  locked,
  publishedBoost,
  focused = false,
  focusEffect
}) {
  const ref = useRef(null);
  useFrame(({ clock }) => {
    if (!ref.current) return;
    const phase = visual.position[0] * 0.7 + visual.position[2] * 0.3;
    const focusActive2 = focused && focusEffect?.enabled;
    const pulseAmp = focusActive2 ? focusEffect.starPulseAmplitude : publishedBoost ? 0.08 : 0.05;
    const pulseSpeed = focusActive2 ? focusEffect.starPulseSpeed : 0.8;
    const pulse = 1 + Math.sin(clock.elapsedTime * pulseSpeed + phase) * pulseAmp;
    ref.current.scale.setScalar(visual.scale * pulse);
  });
  const boostSat = publishedBoost?.saturationBoost ?? 0;
  const boostLight = publishedBoost?.lightnessBoost ?? 0;
  const focusActive = focused && focusEffect?.enabled;
  const saturation = (dimmed || locked ? 25 : 75) + boostSat + (focusActive ? 12 : 0);
  const lightness = (dimmed || locked ? 25 + visual.intensity * 10 : 50 + visual.intensity * 25) + boostLight + (focusActive ? 10 : 0);
  const color = `hsl(${visual.hue}, ${saturation}%, ${lightness}%)`;
  const handleClick = locked ? void 0 : onSelect ? (e) => {
    e.stopPropagation();
    onSelect();
  } : void 0;
  return /* @__PURE__ */ jsxs(
    "mesh",
    {
      ref,
      position: visual.position,
      onClick: handleClick,
      children: [
        /* @__PURE__ */ jsx("sphereGeometry", { args: [0.5, 16, 16] }),
        /* @__PURE__ */ jsx(
          "meshBasicMaterial",
          {
            color,
            toneMapped: false,
            transparent: !!(dimmed || locked),
            opacity: dimmed ? 0.35 : locked ? 0.25 : 1
          }
        )
      ]
    }
  );
}
var DRAG_THRESHOLD_PX = 5;
function DraggableAtomGroup({
  position,
  enabled = false,
  dragPlaneNormal = [0, 1, 0],
  onPositionChange,
  onDragStart,
  onDragEnd,
  onTap,
  children
}) {
  const { camera, gl } = useThree();
  const dragging = useRef(false);
  const dragStarted = useRef(false);
  const pointerStart = useRef({ x: 0, y: 0 });
  const grabOffset = useRef(new THREE.Vector3());
  const plane = useRef(new THREE.Plane());
  const raycaster = useRef(new THREE.Raycaster());
  const intersection = useRef(new THREE.Vector3());
  const latestPosition = useRef(position);
  latestPosition.current = position;
  const updatePlane = useCallback(() => {
    const normal = new THREE.Vector3(...dragPlaneNormal).normalize();
    plane.current.setFromNormalAndCoplanarPoint(
      normal,
      new THREE.Vector3(...latestPosition.current)
    );
  }, [dragPlaneNormal]);
  const pointerToWorld = useCallback(
    (clientX, clientY) => {
      const rect = gl.domElement.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        (clientX - rect.left) / rect.width * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1
      );
      raycaster.current.setFromCamera(ndc, camera);
      if (raycaster.current.ray.intersectPlane(plane.current, intersection.current)) {
        return intersection.current.clone();
      }
      return null;
    },
    [camera, gl]
  );
  const handlePointerDown = useCallback(
    (e) => {
      if (!enabled) return;
      e.stopPropagation();
      dragging.current = true;
      dragStarted.current = false;
      pointerStart.current = { x: e.clientX, y: e.clientY };
      updatePlane();
      const hit = pointerToWorld(e.clientX, e.clientY);
      if (hit) {
        grabOffset.current.set(
          latestPosition.current[0] - hit.x,
          latestPosition.current[1] - hit.y,
          latestPosition.current[2] - hit.z
        );
      }
      e.target.setPointerCapture(e.pointerId);
    },
    [enabled, pointerToWorld, updatePlane]
  );
  const handlePointerMove = useCallback(
    (e) => {
      if (!dragging.current || !enabled) return;
      const dx = e.clientX - pointerStart.current.x;
      const dy = e.clientY - pointerStart.current.y;
      if (!dragStarted.current && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      if (!dragStarted.current) {
        dragStarted.current = true;
        onDragStart?.();
      }
      e.stopPropagation();
      const hit = pointerToWorld(e.clientX, e.clientY);
      if (!hit) return;
      const next = [
        hit.x + grabOffset.current.x,
        hit.y + grabOffset.current.y,
        hit.z + grabOffset.current.z
      ];
      onPositionChange?.(next);
    },
    [enabled, onDragStart, onPositionChange, pointerToWorld]
  );
  const handlePointerUp = useCallback(
    (e) => {
      if (!dragging.current) return;
      dragging.current = false;
      e.target.releasePointerCapture(e.pointerId);
      if (dragStarted.current) {
        e.stopPropagation();
        onDragEnd?.(latestPosition.current);
      } else {
        onTap?.();
      }
      dragStarted.current = false;
    },
    [onDragEnd, onTap]
  );
  const tapTarget = enabled || !!onTap;
  return /* @__PURE__ */ jsxs("group", { position, children: [
    tapTarget ? /* @__PURE__ */ jsxs(
      "mesh",
      {
        onPointerDown: enabled ? handlePointerDown : void 0,
        onPointerMove: enabled ? handlePointerMove : void 0,
        onPointerUp: enabled ? handlePointerUp : void 0,
        onPointerLeave: enabled ? handlePointerUp : void 0,
        onClick: !enabled && onTap ? (e) => {
          e.stopPropagation();
          onTap();
        } : void 0,
        children: [
          /* @__PURE__ */ jsx("sphereGeometry", { args: [enabled ? 0.85 : 1.1, 12, 12] }),
          /* @__PURE__ */ jsx("meshBasicMaterial", { transparent: true, opacity: 0, depthWrite: false })
        ]
      }
    ) : null,
    children
  ] });
}

// src/realms/cosmos/appearance.ts
var DEFAULT_CONSTELLATION_APPEARANCE = {
  titleFontSize: 34,
  titleOpacity: 0.82,
  titleColor: "#c8dce8",
  titleLetterSpacing: 6,
  titleYOffset: -2.5,
  titleBelowPreviewGap: 6,
  titleBelowPreviewFontSize: 22,
  titleDistanceFactor: 12,
  htmlZIndexRange: [8, 0],
  previewWidth: 192,
  previewHeight: 124,
  previewOffsetY: 2.4,
  labelFontSize: 13,
  labelLetterSpacing: 1.2,
  labelMaxWidth: 240,
  labelGap: 10,
  controlsGridHeight: 92,
  billboardDistanceFactor: 10,
  showSceneLabels: false,
  publishedEffect: {
    enabled: true,
    starSaturationBoost: 18,
    starLightnessBoost: 14,
    edgeGlowMultiplier: 1.65,
    dust: {
      enabled: true,
      count: 28,
      orbitRadius: 1.35,
      speed: 0.42,
      color: "#c8dce8",
      opacity: 0.5
    }
  },
  focusEffect: {
    enabled: true,
    starPulseAmplitude: 0.22,
    starPulseSpeed: 2.4,
    glowScale: 1.45,
    glowOpacityBoost: 0.12,
    shockwaveEnabled: true,
    pointLightIntensity: 2.8
  },
  cameraDefaultOffset: [0, 4, 12],
  cameraTargetOffset: [0, 0, 0],
  cameraSlideLerp: 0.035,
  cameraArrivalThreshold: 0.08
};
function mergeConstellationAppearance(overrides) {
  const base = { ...DEFAULT_CONSTELLATION_APPEARANCE, ...overrides };
  if (overrides?.publishedEffect) {
    base.publishedEffect = {
      ...DEFAULT_CONSTELLATION_APPEARANCE.publishedEffect,
      ...overrides.publishedEffect,
      dust: {
        ...DEFAULT_CONSTELLATION_APPEARANCE.publishedEffect.dust,
        ...overrides.publishedEffect.dust
      }
    };
  }
  if (overrides?.focusEffect) {
    base.focusEffect = {
      ...DEFAULT_CONSTELLATION_APPEARANCE.focusEffect,
      ...overrides.focusEffect
    };
  }
  return base;
}
function ConstellationEdge({
  from,
  to,
  hueA,
  hueB,
  gap = 0.55,
  intensityA = 0.5,
  intensityB = 0.5,
  dimmed,
  glowMultiplier = 1
}) {
  const { points, colorA, colorB, glowA, glowB } = useMemo(() => {
    const a = new THREE.Vector3(...from);
    const b = new THREE.Vector3(...to);
    const dir = b.clone().sub(a);
    const len = dir.length();
    if (len < gap * 2.5) {
      return { points: null, colorA: null, colorB: null, glowA: null, glowB: null };
    }
    dir.normalize();
    const start = a.clone().add(dir.clone().multiplyScalar(gap));
    const end = b.clone().sub(dir.clone().multiplyScalar(gap));
    const lA = 55 + intensityA * 20;
    const lB = 55 + intensityB * 20;
    const cA = new THREE.Color(`hsl(${hueA}, 70%, ${lA}%)`);
    const cB = new THREE.Color(`hsl(${hueB}, 70%, ${lB}%)`);
    const gA = new THREE.Color(`hsl(${hueA}, 50%, ${lA + 10}%)`);
    const gB = new THREE.Color(`hsl(${hueB}, 50%, ${lB + 10}%)`);
    return {
      points: [start, end],
      colorA: cA,
      colorB: cB,
      glowA: gA,
      glowB: gB
    };
  }, [from, to, hueA, hueB, gap, intensityA, intensityB]);
  if (!points || !colorA || !colorB || !glowA || !glowB) return null;
  const glowOpacity = (dimmed ? 0.04 : 0.12) * glowMultiplier;
  const coreOpacity = (dimmed ? 0.15 : 0.6) * glowMultiplier;
  return /* @__PURE__ */ jsxs("group", { children: [
    /* @__PURE__ */ jsx(
      Line,
      {
        points,
        vertexColors: [glowA, glowB],
        lineWidth: 4,
        transparent: true,
        opacity: glowOpacity,
        toneMapped: false
      }
    ),
    /* @__PURE__ */ jsx(
      Line,
      {
        points,
        vertexColors: [colorA, colorB],
        lineWidth: 1.5,
        transparent: true,
        opacity: coreOpacity,
        toneMapped: false
      }
    )
  ] });
}
function computeConstellationBounds(positions) {
  if (positions.length === 0) return null;
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  let sumY = 0;
  for (const [x, y, z] of positions) {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minZ = Math.min(minZ, z);
    maxZ = Math.max(maxZ, z);
    sumY += y;
  }
  return {
    center: [(minX + maxX) / 2, sumY / positions.length, (minZ + maxZ) / 2],
    spanX: Math.max(10, maxX - minX + 6),
    spanZ: Math.max(8, maxZ - minZ + 5)
  };
}
var WORLD_TO_SVG = 58;
var ARC_HORIZONTAL_PAD = 72;
function estimateTitlePathWidth(title, fontSize, letterSpacing) {
  const chars = title.trim().length;
  if (chars === 0) return 0;
  const glyphWidth = fontSize * 0.62;
  return chars * glyphWidth + Math.max(0, chars - 1) * letterSpacing + 56;
}
function ConstellationStoryTitleInline({
  title,
  appearance,
  storyOverlay
}) {
  const gradientId = useId().replace(/:/g, "");
  const pathId = `arc-inline-${gradientId}`;
  if (!title.trim()) return null;
  const fontSize = appearance.titleBelowPreviewFontSize;
  const letterSpacing = appearance.titleLetterSpacing * 0.65;
  const minArcSpan = Math.max(
    180,
    estimateTitlePathWidth(title, fontSize, letterSpacing)
  );
  const svgWidth = Math.max(280, Math.ceil(minArcSpan + ARC_HORIZONTAL_PAD * 2));
  const svgHeight = 72;
  const viewPadX = 20;
  const viewPadY = 8;
  const centerX = svgWidth / 2;
  const arcY = svgHeight * 0.86;
  const arcPeak = svgHeight * 0.12;
  const arcStartX = centerX - minArcSpan / 2;
  const arcEndX = centerX + minArcSpan / 2;
  const arcMidX = centerX;
  const arcPath = `M ${arcStartX} ${arcY} Q ${arcMidX} ${arcPeak} ${arcEndX} ${arcY}`;
  const editable = !!(storyOverlay?.editable && storyOverlay.onEditClick);
  const isSystemOverlay = storyOverlay?.overlayMode === "system";
  const hasDirector = !isSystemOverlay && !!(storyOverlay?.directorName && storyOverlay.directorName.trim());
  const hasRatingGenre = !isSystemOverlay && !!(storyOverlay?.contentRating || storyOverlay?.genre);
  const hasVersion = storyOverlay?.publishedVersion != null && storyOverlay.publishedVersion > 0;
  const hasPublish = !!(storyOverlay?.onPublish && storyOverlay.publishLabel);
  const showOverlay = isSystemOverlay ? hasVersion || hasPublish : hasDirector || hasRatingGenre || hasVersion || hasPublish;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        marginTop: appearance.titleBelowPreviewGap,
        pointerEvents: editable ? "auto" : "none"
      },
      children: [
        /* @__PURE__ */ jsx(
          StoryTitleArcSvg,
          {
            title,
            arcPath,
            gradientId,
            pathId,
            svgWidth,
            svgHeight,
            viewPadX,
            viewPadY,
            fontSize,
            letterSpacing,
            opacity: appearance.titleOpacity,
            editable,
            editAriaLabel: storyOverlay?.editAriaLabel,
            onEditClick: storyOverlay?.onEditClick
          }
        ),
        showOverlay ? /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
              maxWidth: svgWidth,
              pointerEvents: "none"
            },
            children: [
              hasDirector ? /* @__PURE__ */ jsx(
                DirectorCredit,
                {
                  prefix: storyOverlay.chosenByPrefix ?? "",
                  name: storyOverlay.directorName,
                  avatarUrl: storyOverlay.directorAvatarUrl ?? null,
                  avatarFallback: storyOverlay.directorAvatarFallback
                }
              ) : null,
              hasRatingGenre ? /* @__PURE__ */ jsx(
                RatingGenreLine,
                {
                  contentRating: storyOverlay.contentRating ?? null,
                  genre: storyOverlay.genre ?? null,
                  separator: storyOverlay.ratingGenreSeparator ?? " \xB7 "
                }
              ) : null,
              /* @__PURE__ */ jsx(
                PublishMetaLine,
                {
                  publishedVersion: storyOverlay.publishedVersion,
                  publishedVersionLabel: storyOverlay.publishedVersionLabel,
                  publishLabel: storyOverlay.publishLabel,
                  onPublish: storyOverlay.onPublish
                }
              )
            ]
          }
        ) : null
      ]
    }
  );
}
function StoryTitleArcSvg({
  title,
  arcPath,
  gradientId,
  pathId,
  svgWidth,
  svgHeight,
  viewPadX,
  viewPadY,
  fontSize,
  letterSpacing,
  opacity,
  editable,
  editAriaLabel,
  onEditClick
}) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: svgWidth,
      height: svgHeight,
      viewBox: `${-viewPadX} ${-viewPadY} ${svgWidth + viewPadX * 2} ${svgHeight + viewPadY}`,
      style: {
        overflow: "visible",
        display: "block",
        cursor: editable ? "pointer" : void 0
      },
      "aria-hidden": editable ? void 0 : true,
      role: editable ? "button" : void 0,
      tabIndex: editable ? 0 : void 0,
      "aria-label": editable ? editAriaLabel : void 0,
      onClick: editable ? onEditClick : void 0,
      onKeyDown: editable ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEditClick?.();
        }
      } : void 0,
      children: [
        /* @__PURE__ */ jsxs("defs", { children: [
          /* @__PURE__ */ jsxs("linearGradient", { id: gradientId, x1: "0%", y1: "0%", x2: "100%", y2: "0%", children: [
            /* @__PURE__ */ jsx("stop", { offset: "0%", stopColor: "#8ab4c8" }),
            /* @__PURE__ */ jsx("stop", { offset: "35%", stopColor: "#c8d8e4" }),
            /* @__PURE__ */ jsx("stop", { offset: "65%", stopColor: "#e8dcc8" }),
            /* @__PURE__ */ jsx("stop", { offset: "100%", stopColor: "#8ab4c8" })
          ] }),
          /* @__PURE__ */ jsx("path", { id: pathId, d: arcPath, fill: "none" })
        ] }),
        /* @__PURE__ */ jsx(
          "text",
          {
            fill: `url(#${gradientId})`,
            fontFamily: "'Palatino Linotype', Palatino, 'Book Antiqua', Georgia, serif",
            fontSize,
            fontWeight: 700,
            fontStyle: "italic",
            letterSpacing,
            opacity,
            stroke: "rgba(20,40,60,0.35)",
            strokeWidth: 0.6,
            paintOrder: "stroke fill",
            style: { pointerEvents: "none" },
            children: /* @__PURE__ */ jsx("textPath", { href: `#${pathId}`, startOffset: "50%", textAnchor: "middle", children: title.toUpperCase() })
          }
        )
      ]
    }
  );
}
function ConstellationTitle({
  title,
  bounds,
  appearance,
  arcFrom,
  arcTo,
  storyOverlay
}) {
  const gradientId = useId().replace(/:/g, "");
  const pathId = `arc-${gradientId}`;
  if (!title.trim()) return null;
  const titlePos = bounds.center;
  const minArcSpan = Math.max(
    120,
    estimateTitlePathWidth(
      title,
      appearance.titleFontSize,
      appearance.titleLetterSpacing
    )
  );
  const titleOffsetX = arcFrom != null ? (arcFrom[0] - bounds.center[0]) * WORLD_TO_SVG : -minArcSpan / 2;
  const endOffsetX = arcTo != null ? (arcTo[0] - bounds.center[0]) * WORLD_TO_SVG : minArcSpan / 2;
  const arcSpanNeeded = Math.max(
    minArcSpan,
    Math.abs(endOffsetX - titleOffsetX) + 48
  );
  const svgWidth = Math.max(
    minArcSpan + ARC_HORIZONTAL_PAD * 2,
    Math.ceil(arcSpanNeeded + ARC_HORIZONTAL_PAD * 2)
  );
  const svgHeight = 110;
  const viewPadX = 28;
  const viewPadY = 10;
  const centerX = svgWidth / 2;
  const arcY = svgHeight * 0.14;
  const arcPeak = svgHeight * 0.92;
  let arcStartX = centerX + titleOffsetX;
  let arcEndX = centerX + endOffsetX;
  if (arcStartX > arcEndX) {
    const swap = arcStartX;
    arcStartX = arcEndX;
    arcEndX = swap;
  }
  if (arcEndX - arcStartX < minArcSpan) {
    const mid = (arcStartX + arcEndX) / 2;
    arcStartX = mid - minArcSpan / 2;
    arcEndX = mid + minArcSpan / 2;
  }
  const arcMargin = ARC_HORIZONTAL_PAD / 2;
  arcStartX = Math.max(arcMargin, arcStartX);
  arcEndX = Math.min(svgWidth - arcMargin, arcEndX);
  if (arcEndX - arcStartX < minArcSpan) {
    arcStartX = centerX - minArcSpan / 2;
    arcEndX = centerX + minArcSpan / 2;
  }
  const arcMidX = (arcStartX + arcEndX) / 2;
  const arcPath = `M ${arcStartX} ${arcY} Q ${arcMidX} ${arcPeak} ${arcEndX} ${arcY}`;
  const arcPathWidth = arcEndX - arcStartX;
  const baseLetterSpacing = appearance.titleLetterSpacing;
  const chars = title.trim().length;
  let letterSpacing = baseLetterSpacing;
  if (chars > 1 && arcPathWidth > 0) {
    const glyphWidth = appearance.titleFontSize * 0.62;
    const textWidth = chars * glyphWidth + Math.max(0, chars - 1) * baseLetterSpacing + 56;
    const targetWidth = arcPathWidth * 0.92;
    if (textWidth < targetWidth) {
      letterSpacing = (targetWidth - chars * glyphWidth - 56) / Math.max(1, chars - 1);
      letterSpacing = Math.max(baseLetterSpacing, letterSpacing);
    }
  }
  const editable = !!(storyOverlay?.editable && storyOverlay.onEditClick);
  const isSystemOverlay = storyOverlay?.overlayMode === "system";
  const hasDirector = !isSystemOverlay && !!(storyOverlay?.directorName && storyOverlay.directorName.trim());
  const hasRatingGenre = !isSystemOverlay && !!(storyOverlay?.contentRating || storyOverlay?.genre);
  const hasVersion = storyOverlay?.publishedVersion != null && storyOverlay.publishedVersion > 0;
  const hasPublish = !!(storyOverlay?.onPublish && storyOverlay.publishLabel);
  const showOverlay = isSystemOverlay ? hasVersion || hasPublish : hasDirector || hasRatingGenre || hasVersion || hasPublish;
  const titleControl = /* @__PURE__ */ jsx(
    StoryTitleArcSvg,
    {
      title,
      arcPath,
      gradientId,
      pathId,
      svgWidth,
      svgHeight,
      viewPadX,
      viewPadY,
      fontSize: appearance.titleFontSize,
      letterSpacing,
      opacity: appearance.titleOpacity,
      editable,
      editAriaLabel: storyOverlay?.editAriaLabel,
      onEditClick: storyOverlay?.onEditClick
    }
  );
  const anchorPos = arcFrom ?? titlePos;
  return /* @__PURE__ */ jsx(
    Billboard,
    {
      position: [
        anchorPos[0],
        anchorPos[1] + appearance.titleYOffset,
        anchorPos[2]
      ],
      children: /* @__PURE__ */ jsx(
        Html,
        {
          center: true,
          transform: true,
          distanceFactor: appearance.titleDistanceFactor,
          zIndexRange: appearance.htmlZIndexRange,
          occlude: false,
          style: {
            pointerEvents: editable ? "auto" : "none",
            userSelect: "none"
          },
          children: /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
                marginTop: 4
              },
              children: [
                titleControl,
                showOverlay ? /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      maxWidth: svgWidth,
                      marginTop: 2,
                      pointerEvents: "none"
                    },
                    children: [
                      hasDirector ? /* @__PURE__ */ jsx(
                        DirectorCredit,
                        {
                          prefix: storyOverlay.chosenByPrefix ?? "",
                          name: storyOverlay.directorName,
                          avatarUrl: storyOverlay.directorAvatarUrl ?? null,
                          avatarFallback: storyOverlay.directorAvatarFallback
                        }
                      ) : null,
                      hasRatingGenre ? /* @__PURE__ */ jsx(
                        RatingGenreLine,
                        {
                          contentRating: storyOverlay.contentRating ?? null,
                          genre: storyOverlay.genre ?? null,
                          separator: storyOverlay.ratingGenreSeparator ?? " \xB7 "
                        }
                      ) : null,
                      /* @__PURE__ */ jsx(
                        PublishMetaLine,
                        {
                          publishedVersion: storyOverlay.publishedVersion,
                          publishedVersionLabel: storyOverlay.publishedVersionLabel,
                          publishLabel: storyOverlay.publishLabel,
                          onPublish: storyOverlay.onPublish
                        }
                      )
                    ]
                  }
                ) : null
              ]
            }
          )
        }
      )
    }
  );
}
function DirectorCredit({
  prefix,
  name,
  avatarUrl,
  avatarFallback
}) {
  const initial = name.trim().charAt(0).toUpperCase() || "?";
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11,
        color: "rgba(232,240,255,0.72)",
        letterSpacing: 0.35,
        textShadow: "0 1px 8px rgba(0,0,0,0.95)",
        whiteSpace: "nowrap"
      },
      children: [
        /* @__PURE__ */ jsx("span", { style: { opacity: 0.85 }, children: prefix }),
        /* @__PURE__ */ jsx(
          "span",
          {
            style: {
              width: 20,
              height: 20,
              borderRadius: "50%",
              overflow: "hidden",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.1)",
              border: "1px solid rgba(126,184,255,0.35)",
              fontSize: 9,
              fontWeight: 700,
              color: "rgba(232,240,255,0.9)"
            },
            children: avatarUrl ? /* @__PURE__ */ jsx(
              "img",
              {
                src: avatarUrl,
                alt: "",
                style: { width: "100%", height: "100%", objectFit: "cover" }
              }
            ) : avatarFallback ? avatarFallback : initial
          }
        ),
        /* @__PURE__ */ jsx("span", { style: { fontWeight: 600 }, children: name })
      ]
    }
  );
}
function RatingGenreLine({
  contentRating,
  genre,
  separator
}) {
  const parts = [];
  if (contentRating) {
    parts.push(
      /* @__PURE__ */ jsx("span", { style: { fontWeight: 700 }, children: contentRating }, "rating")
    );
  }
  if (genre) {
    parts.push(/* @__PURE__ */ jsx("span", { children: genre }, "genre"));
  }
  if (parts.length === 0) return null;
  return /* @__PURE__ */ jsx(
    "div",
    {
      style: {
        display: "inline-flex",
        alignItems: "center",
        gap: 0,
        fontSize: 10,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        color: "rgba(232,240,255,0.55)",
        textShadow: "0 1px 6px rgba(0,0,0,0.95)"
      },
      children: parts.map((part, i) => /* @__PURE__ */ jsxs("span", { children: [
        i > 0 ? separator : null,
        part
      ] }, i))
    }
  );
}
function PublishMetaLine({
  publishedVersion,
  publishedVersionLabel,
  publishLabel,
  onPublish
}) {
  const hasVersion = publishedVersion != null && publishedVersion > 0;
  const hasPublish = !!(onPublish && publishLabel);
  if (!hasVersion && !hasPublish) return null;
  const versionText = publishedVersionLabel ? `${publishedVersionLabel} v${publishedVersion}` : `v${publishedVersion}`;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        marginTop: 4,
        pointerEvents: "auto"
      },
      children: [
        hasPublish ? /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: onPublish,
            style: {
              background: "rgba(126,184,255,0.12)",
              border: "1px solid rgba(126,184,255,0.45)",
              borderRadius: 12,
              padding: "3px 10px",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "rgba(232,240,255,0.9)",
              cursor: "pointer",
              backdropFilter: "blur(4px)",
              transition: "border-color 200ms, background 200ms"
            },
            children: publishLabel
          }
        ) : null,
        hasVersion ? /* @__PURE__ */ jsx(
          "span",
          {
            style: {
              fontSize: 10,
              letterSpacing: 0.5,
              color: "rgba(232,240,255,0.5)",
              textTransform: "uppercase"
            },
            children: versionText
          }
        ) : null
      ]
    }
  );
}
function StarBillboard({
  label,
  appearance,
  previewImageUrl,
  previewContent,
  dimmed,
  focused,
  visible = true,
  controlsSlot,
  storyTitleSlot,
  onPreviewClick,
  labelOnly = false
}) {
  if (!visible) return null;
  if (labelOnly && label) {
    return /* @__PURE__ */ jsx(
      Html,
      {
        position: [0, 2.6, 0],
        center: true,
        zIndexRange: appearance.htmlZIndexRange,
        occlude: false,
        style: { pointerEvents: "none", userSelect: "none" },
        children: /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              fontFamily: "'Inter', sans-serif",
              fontSize: focused ? "15px" : "13px",
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: focused ? "#eef4ff" : dimmed ? "rgba(210,220,240,0.82)" : "#e8f0ff",
              textAlign: "center",
              padding: focused ? "6px 14px" : "5px 12px",
              borderRadius: 6,
              background: focused ? "rgba(8,18,36,0.96)" : "rgba(4,10,20,0.9)",
              border: focused ? "1px solid rgba(126,184,255,0.65)" : "1px solid rgba(126,184,255,0.32)",
              boxShadow: focused ? "0 0 20px rgba(126,184,255,0.35), 0 4px 18px rgba(0,0,0,0.9)" : "0 2px 14px rgba(0,0,0,0.85)",
              whiteSpace: "nowrap",
              lineHeight: 1.2,
              maxWidth: 240,
              overflow: "hidden",
              textOverflow: "ellipsis"
            },
            children: label
          }
        )
      }
    );
  }
  const borderColor = focused ? "rgba(126,184,255,0.75)" : "rgba(126,184,255,0.22)";
  const previewOpacity = focused ? 1 : dimmed ? 0.22 : 0.34;
  const previewFilter = focused ? "brightness(1.08) saturate(1.1)" : "brightness(0.72) saturate(0.65)";
  const hasControls = !!(focused && controlsSlot);
  const previewClickable = !!onPreviewClick;
  return /* @__PURE__ */ jsxs(
    Html,
    {
      position: [0, appearance.previewOffsetY, 0],
      center: true,
      transform: true,
      distanceFactor: appearance.billboardDistanceFactor,
      zIndexRange: appearance.htmlZIndexRange,
      occlude: false,
      style: {
        pointerEvents: previewClickable || hasControls || !!storyTitleSlot ? "auto" : "none",
        userSelect: "none"
      },
      children: [
        /* @__PURE__ */ jsx("style", { children: `
        @keyframes chuziPreviewFocusIn {
          0% { opacity: 0.4; transform: scale(0.9); filter: brightness(0.8) saturate(0.7); }
          55% { opacity: 1; transform: scale(1.05); filter: brightness(1.12) saturate(1.15); }
          100% { opacity: 1; transform: scale(1); filter: brightness(1.08) saturate(1.1); }
        }
        @keyframes chuziPreviewFocusOut {
          0% { opacity: 1; transform: scale(1); }
          100% { opacity: ${previewOpacity}; transform: scale(0.96); }
        }
      ` }),
        /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `translateY(-${appearance.previewHeight / 2}px)`
            },
            children: [
              /* @__PURE__ */ jsx(
                "div",
                {
                  role: previewClickable ? "button" : void 0,
                  tabIndex: previewClickable ? 0 : void 0,
                  "aria-label": previewClickable ? label || void 0 : void 0,
                  onClick: previewClickable ? (e) => {
                    e.stopPropagation();
                    onPreviewClick?.();
                  } : void 0,
                  onKeyDown: previewClickable ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      onPreviewClick?.();
                    }
                  } : void 0,
                  style: {
                    width: appearance.previewWidth,
                    height: appearance.previewHeight,
                    borderRadius: hasControls ? "8px 8px 0 0" : 8,
                    overflow: "hidden",
                    border: `1px solid ${borderColor}`,
                    borderBottom: hasControls ? "none" : `1px solid ${borderColor}`,
                    background: "rgba(4,7,13,0.92)",
                    boxShadow: focused ? "0 0 28px rgba(126,184,255,0.45), 0 4px 24px rgba(0,0,0,0.5)" : "0 4px 20px rgba(0,0,0,0.55)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: previewOpacity,
                    filter: previewFilter,
                    transition: "border-color 0.35s ease, box-shadow 0.35s ease",
                    animation: focused ? "chuziPreviewFocusIn 0.48s cubic-bezier(0.34, 1.45, 0.64, 1) forwards" : "chuziPreviewFocusOut 0.32s ease forwards",
                    cursor: previewClickable ? "pointer" : void 0
                  },
                  children: previewImageUrl ? /* @__PURE__ */ jsx(
                    "img",
                    {
                      src: previewImageUrl,
                      alt: "",
                      style: {
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block"
                      }
                    }
                  ) : previewContent ?? /* @__PURE__ */ jsx(
                    "div",
                    {
                      style: {
                        width: "100%",
                        height: "100%",
                        background: "radial-gradient(ellipse at 30% 20%, rgba(80,120,200,0.2) 0%, rgba(10,14,30,0.95) 70%)"
                      }
                    }
                  )
                },
                focused ? "focused" : "unfocused"
              ),
              hasControls ? /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    width: appearance.previewWidth,
                    pointerEvents: "auto",
                    flexShrink: 0
                  },
                  children: controlsSlot
                }
              ) : null,
              focused && storyTitleSlot ? storyTitleSlot : null,
              appearance.showSceneLabels && label ? /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    marginTop: appearance.labelGap,
                    fontSize: appearance.labelFontSize,
                    fontWeight: 700,
                    letterSpacing: appearance.labelLetterSpacing,
                    textTransform: "uppercase",
                    color: "rgba(232,240,255,0.96)",
                    textAlign: "center",
                    textShadow: "0 0 18px rgba(0,0,0,0.95), 0 2px 12px rgba(0,0,0,0.95), 0 0 6px rgba(126,184,255,0.25)",
                    whiteSpace: "normal",
                    lineHeight: 1.2,
                    maxWidth: appearance.labelMaxWidth,
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical"
                  },
                  children: label
                }
              ) : null
            ]
          }
        )
      ]
    }
  );
}
function PublishedConstellationAura({
  bounds,
  dust
}) {
  const groupRef = useRef(null);
  const particles = useMemo(() => {
    const items = [];
    for (let i = 0; i < dust.count; i++) {
      items.push({
        angle: i / dust.count * Math.PI * 2,
        radius: dust.orbitRadius * (0.65 + i % 5 * 0.08),
        yOffset: (i % 7 - 3) * 0.35,
        phase: i * 0.73 % (Math.PI * 2),
        size: 0.035 + i % 4 * 0.015,
        drift: 0.6 + i % 3 * 0.25
      });
    }
    return items;
  }, [dust.count, dust.orbitRadius]);
  useFrame(({ clock }) => {
    if (!groupRef.current) return;
    const t = clock.elapsedTime * dust.speed;
    const rx = bounds.spanX * 0.5;
    const rz = bounds.spanZ * 0.5;
    groupRef.current.children.forEach((child, i) => {
      const p = particles[i];
      if (!p) return;
      const a = p.angle + t * p.drift + p.phase;
      child.position.set(
        Math.cos(a) * rx * p.radius,
        p.yOffset + Math.sin(t * 1.4 + p.phase) * 0.45,
        Math.sin(a) * rz * p.radius
      );
    });
  });
  return /* @__PURE__ */ jsx("group", { ref: groupRef, position: bounds.center, children: particles.map((p, i) => /* @__PURE__ */ jsxs("mesh", { children: [
    /* @__PURE__ */ jsx("sphereGeometry", { args: [p.size, 6, 6] }),
    /* @__PURE__ */ jsx(
      "meshBasicMaterial",
      {
        color: dust.color,
        transparent: true,
        opacity: dust.opacity,
        toneMapped: false,
        depthWrite: false
      }
    )
  ] }, i)) });
}
function Constellation({
  scenes,
  storyTitle,
  storyOverlay,
  edges = [],
  appearance: appearanceOverrides,
  showOverlays = true,
  showStoryTitle = true,
  published = false,
  storyFocused = false,
  onSceneSelect,
  onScenePositionChange,
  onSceneDragStart,
  onSceneDragEnd,
  onScenePositionCommit
}) {
  const appearance = mergeConstellationAppearance(appearanceOverrides);
  const publishedActive = published && appearance.publishedEffect.enabled;
  const publishedBoost = publishedActive ? {
    saturationBoost: appearance.publishedEffect.starSaturationBoost,
    lightnessBoost: appearance.publishedEffect.starLightnessBoost
  } : void 0;
  const edgeGlowMultiplier = publishedActive ? appearance.publishedEffect.edgeGlowMultiplier : 1;
  const focusEffect = storyFocused && appearance.focusEffect.enabled ? appearance.focusEffect : void 0;
  const sceneMap = new Map(scenes.map((s, i) => [s.id, { entry: s, index: i }]));
  const scenePositions = scenes.map((s) => s.visual.position);
  const bounds = computeConstellationBounds(scenePositions);
  const titleScene = scenes.find((s) => s.isTitle) ?? scenes[0];
  scenes[scenes.length - 1];
  const arcFrom = titleScene?.visual.position;
  const arcTo = titleScene?.visual.position;
  const resolvedEdges = [];
  for (const edge of edges) {
    const src = sceneMap.get(edge.source);
    const tgt = sceneMap.get(edge.target);
    if (src && tgt) {
      resolvedEdges.push({ from: src.entry, to: tgt.entry, type: edge.type });
    }
  }
  return /* @__PURE__ */ jsxs("group", { children: [
    publishedActive && appearance.publishedEffect.dust.enabled && bounds ? /* @__PURE__ */ jsx(
      PublishedConstellationAura,
      {
        bounds,
        dust: appearance.publishedEffect.dust
      }
    ) : null,
    showOverlays && showStoryTitle && storyTitle && bounds ? /* @__PURE__ */ jsx(
      ConstellationTitle,
      {
        title: storyTitle,
        bounds,
        appearance,
        arcFrom,
        arcTo,
        storyOverlay
      }
    ) : null,
    scenes.map((entry, i) => /* @__PURE__ */ jsxs(
      DraggableAtomGroup,
      {
        position: entry.visual.position,
        enabled: !!entry.draggable && !!(onScenePositionChange || onScenePositionCommit),
        onPositionChange: onScenePositionChange ? (pos) => onScenePositionChange(entry.id, pos) : void 0,
        onDragStart: onSceneDragStart ? () => onSceneDragStart(entry.id) : void 0,
        onDragEnd: onSceneDragEnd || onScenePositionCommit ? (pos) => {
          onSceneDragEnd?.(entry.id);
          onScenePositionCommit?.(entry.id, pos);
        } : void 0,
        onTap: onSceneSelect && !entry.locked ? () => onSceneSelect(i) : void 0,
        children: [
          /* @__PURE__ */ jsx(
            Star,
            {
              visual: { ...entry.visual, position: [0, 0, 0] },
              dimmed: entry.dimmed,
              locked: entry.locked,
              publishedBoost,
              focused: storyFocused,
              focusEffect,
              onSelect: !entry.draggable && onSceneSelect && !entry.locked ? () => onSceneSelect(i) : void 0
            }
          ),
          showOverlays && (entry.labelOnly || entry.label || entry.previewImageUrl || entry.previewContent) ? /* @__PURE__ */ jsx(
            StarBillboard,
            {
              label: entry.label ?? "",
              appearance,
              previewImageUrl: entry.previewImageUrl,
              previewContent: entry.previewContent,
              dimmed: entry.dimmed,
              focused: entry.focused,
              labelOnly: entry.labelOnly,
              controlsSlot: entry.controlsSlot,
              onPreviewClick: onSceneSelect && !entry.locked ? () => onSceneSelect(i) : void 0,
              visible: showOverlays
            }
          ) : null
        ]
      },
      entry.id
    )),
    resolvedEdges.map(({ from, to, type }) => {
      const edgeDimmed = !!(from.dimmed || to.dimmed);
      return /* @__PURE__ */ jsx(
        ConstellationEdge,
        {
          from: from.visual.position,
          to: to.visual.position,
          hueA: from.visual.hue,
          hueB: to.visual.hue,
          intensityA: from.visual.intensity,
          intensityB: to.visual.intensity,
          dimmed: edgeDimmed,
          glowMultiplier: edgeGlowMultiplier
        },
        `edge-${from.id}-${to.id}-${type}`
      );
    })
  ] });
}

// src/realms/cosmos/index.ts
var GENRE_HUE_OFFSET = {
  drama: 50,
  thriller: 220,
  horror: 0,
  comedy: 35,
  romance: 340,
  scifi: 200,
  documentary: 180,
  animation: 280
};
var MOOD_HUE = {
  bright: 50,
  warm: 25,
  bittersweet: 290,
  somber: 230,
  tense: 210,
  playful: 110,
  melancholy: 250
};
function clamp01(n) {
  return Math.max(0, Math.min(1, n));
}
function popularityToIntensity(film) {
  const watches = Math.max(0, film.watch_starts_count);
  const log = Math.log10(1 + watches);
  return clamp01(log / 6);
}
function deriveScale(film) {
  const scenes = film.scenes_count ?? 1;
  return clamp01(0.25 + Math.log10(1 + scenes) / 4);
}
function deriveHue(film) {
  const genreKey = (film.genre ?? "").toLowerCase().replace(/[^a-z]/g, "");
  const moodKey = "";
  const moodHue = MOOD_HUE[moodKey];
  const genreOffset = GENRE_HUE_OFFSET[genreKey];
  if (moodHue !== void 0) return moodHue;
  if (genreOffset !== void 0) return genreOffset;
  return 210;
}
function deriveState(film) {
  return film.published ? "default" : "new";
}
var cosmosMapping = (film) => ({
  position: [0, 0, 0],
  // assigned by the realm's spatial layouter
  scale: deriveScale(film),
  hue: deriveHue(film),
  intensity: popularityToIntensity(film),
  state: deriveState(film),
  metadata: {
    title: film.title,
    popularity: film.watch_starts_count,
    genre: film.genre
  }
});

// src/realms/cosmos/components/layout.ts
function distributeStars(count, options = {}) {
  const radius = options.radius ?? 25;
  const thickness = options.thickness ?? 4;
  const random = options.seed !== void 0 ? mulberry32(options.seed) : Math.random;
  const positions = [];
  for (let i = 0; i < count; i++) {
    const r = Math.sqrt(random()) * radius;
    const theta = random() * Math.PI * 2;
    const y = (random() - 0.5) * thickness;
    positions.push([Math.cos(theta) * r, y, Math.sin(theta) * r]);
  }
  return positions;
}
function mulberry32(seed) {
  let s = seed >>> 0;
  return () => {
    s = s + 1831565813 >>> 0;
    let t = s;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
var COSMOS_SCENE_LEVEL_SPACING = 7;
var COSMOS_BRANCH_SIBLING_SPACING = 5.5;
var COSMOS_MIN_NODE_CLEARANCE = 5;
var COSMOS_MIN_EDGE_CLEARANCE = 2.2;
var COSMOS_SCENE_BRANCH_SPACING = 1.6;
var COSMOS_MAX_SCENE_EDGE_LENGTH = 14;
var COSMOS_CONSTELLATION_GAP_SCENES = 5;
var COSMOS_DEFAULT_FOOTPRINT_RADIUS = 10;
var COSMOS_CONSTELLATION_VISUAL_PADDING = 6;
var GOLDEN_ANGLE = 2.399963229728653;
function sortScenesForConstellationLayout(sceneList) {
  return [...sceneList].sort((a, b) => {
    if (a.is_title !== b.is_title) return a.is_title ? -1 : 1;
    const orderA = a.order ?? 0;
    const orderB = b.order ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    return (a.created_at ?? "").localeCompare(b.created_at ?? "");
  });
}
function orderScenesForConstellationLayout(sceneList, graph) {
  if (!graph?.nodes?.length) return sortScenesForConstellationLayout(sceneList);
  const sceneById = new Map(sceneList.map((scene) => [scene.id, scene]));
  const ordered = [];
  const seen = /* @__PURE__ */ new Set();
  const sortedNodes = [...graph.nodes].sort((a, b) => {
    if (a.level !== b.level) return a.level - b.level;
    if (a.x !== b.x) return a.x - b.x;
    return (a.index ?? 0) - (b.index ?? 0);
  });
  for (const node of sortedNodes) {
    const scene = sceneById.get(node.id);
    if (scene && !seen.has(scene.id)) {
      ordered.push(scene);
      seen.add(scene.id);
    }
  }
  for (const scene of sortScenesForConstellationLayout(sceneList)) {
    if (!seen.has(scene.id)) {
      ordered.push(scene);
      seen.add(scene.id);
    }
  }
  return ordered;
}
function hashSceneId(id, salt = "") {
  let h = 0;
  const key = id + salt;
  for (let i = 0; i < key.length; i++) {
    h = h * 31 + key.charCodeAt(i) >>> 0;
  }
  return h / 4294967296;
}
function clampEdgeLength(positions, sourceId, targetId, maxLength) {
  const src = positions.get(sourceId);
  const tgt = positions.get(targetId);
  if (!src || !tgt) return;
  const dx = tgt[0] - src[0];
  const dy = tgt[1] - src[1];
  const dz = tgt[2] - src[2];
  const len = Math.hypot(dx, dy, dz);
  if (len <= maxLength || len === 0) return;
  const scale = maxLength / len;
  positions.set(targetId, [
    src[0] + dx * scale,
    src[1] + dy * scale,
    src[2] + dz * scale
  ]);
}
function buildChildrenByParent(edges) {
  const childrenByParent = /* @__PURE__ */ new Map();
  for (const edge of edges) {
    const kids = childrenByParent.get(edge.source) ?? [];
    if (!kids.includes(edge.target)) {
      kids.push(edge.target);
    }
    childrenByParent.set(edge.source, kids);
  }
  return childrenByParent;
}
function sceneLevelJitter(sceneId) {
  return (hashSceneId(sceneId, "y") - 0.5) * 1.2;
}
function distPointSegment2D(px, pz, ax, az, bx, bz) {
  const dx = bx - ax;
  const dz = bz - az;
  const lenSq = dx * dx + dz * dz;
  if (lenSq === 0) return Math.hypot(px - ax, pz - az);
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / lenSq));
  const cx = ax + t * dx;
  const cz = az + t * dz;
  return Math.hypot(px - cx, pz - cz);
}
function distSegmentSegment2D(a, b) {
  if (a.ax === a.bx && a.az === a.bz || b.ax === b.bx && b.az === b.bz) {
    const pointSeg = a.ax === a.bx && a.az === a.bz ? b : a;
    const px = a.ax === a.bx && a.az === a.bz ? a.ax : b.ax;
    const pz = a.ax === a.bx && a.az === a.bz ? a.az : b.az;
    return distPointSegment2D(
      px,
      pz,
      pointSeg.ax,
      pointSeg.az,
      pointSeg.bx,
      pointSeg.bz
    );
  }
  const checks = [
    distPointSegment2D(a.ax, a.az, b.ax, b.az, b.bx, b.bz),
    distPointSegment2D(a.bx, a.bz, b.ax, b.az, b.bx, b.bz),
    distPointSegment2D(b.ax, b.az, a.ax, a.az, a.bx, a.bz),
    distPointSegment2D(b.bx, b.bz, a.ax, a.az, a.bx, a.bz)
  ];
  return Math.min(...checks);
}
function collectEdgeSegments(positions, edges, exclude) {
  const segments = [];
  for (const edge of edges) {
    if (exclude && edge.source === exclude.source && edge.target === exclude.target) {
      continue;
    }
    const src = positions.get(edge.source);
    const tgt = positions.get(edge.target);
    if (!src || !tgt) continue;
    segments.push({ ax: src[0], az: src[2], bx: tgt[0], bz: tgt[2] });
  }
  return segments;
}
function edgeClearanceOk(positions, edges, sourceId, targetId, minClearance) {
  const src = positions.get(sourceId);
  const tgt = positions.get(targetId);
  if (!src || !tgt) return true;
  const candidate = {
    ax: src[0],
    az: src[2],
    bx: tgt[0],
    bz: tgt[2]
  };
  const existing = collectEdgeSegments(positions, edges, {
    source: sourceId,
    target: targetId
  });
  return existing.every(
    (seg) => distSegmentSegment2D(candidate, seg) >= minClearance || seg.ax === candidate.ax && seg.az === candidate.az && seg.bx === candidate.bx && seg.bz === candidate.bz
  );
}
function nodeClearanceOk(positions, sceneId, pos, minClearance) {
  for (const [otherId, otherPos] of positions) {
    if (otherId === sceneId) continue;
    if (Math.hypot(pos[0] - otherPos[0], pos[2] - otherPos[2]) < minClearance) {
      return false;
    }
  }
  return true;
}
function organicChildOffset(parentId, childId, childIndex, siblingCount, nodeCount) {
  const fanSpread = Math.min(
    Math.PI * 0.9,
    0.28 * siblingCount + 0.22 * Math.sqrt(nodeCount)
  );
  const mid = (siblingCount - 1) / 2;
  const baseAngle = hashSceneId(parentId, "fan") * Math.PI * 2 + (childIndex - mid) * (fanSpread / Math.max(siblingCount - 1, 1));
  const reach = COSMOS_BRANCH_SIBLING_SPACING * (0.75 + hashSceneId(childId, "reach") * 0.65 + Math.min(nodeCount, 8) * 0.04);
  return [Math.cos(baseAngle) * reach, Math.sin(baseAngle) * reach * 0.18];
}
function placeChildScene(parentPos, parentId, childId, childIndex, siblingCount, nodeCount) {
  const depthStep = COSMOS_SCENE_LEVEL_SPACING * (0.82 + hashSceneId(childId, "depth") * 0.28);
  const [ox, oz] = organicChildOffset(
    parentId,
    childId,
    childIndex,
    siblingCount,
    nodeCount
  );
  return [
    parentPos[0] + ox,
    parentPos[1] + sceneLevelJitter(childId),
    parentPos[2] - depthStep + oz
  ];
}
function resolveNodeOverlaps(positions, minClearance, pinnedIds = /* @__PURE__ */ new Set()) {
  const ids = [...positions.keys()];
  for (let pass = 0; pass < 6; pass++) {
    let moved = false;
    for (let i = 0; i < ids.length; i++) {
      for (let j = i + 1; j < ids.length; j++) {
        const aId = ids[i];
        const bId = ids[j];
        const a = positions.get(aId);
        const b = positions.get(bId);
        const dx = b[0] - a[0];
        const dz = b[2] - a[2];
        const dist = Math.hypot(dx, dz);
        if (dist >= minClearance || dist === 0) continue;
        const push = (minClearance - dist) * 0.55;
        const nx = dx / (dist || 1);
        const nz = dz / (dist || 1);
        if (!pinnedIds.has(aId)) {
          positions.set(aId, [a[0] - nx * push, a[1], a[2] - nz * push]);
        }
        if (!pinnedIds.has(bId)) {
          positions.set(bId, [b[0] + nx * push, b[1], b[2] + nz * push]);
        }
        moved = true;
      }
    }
    if (!moved) break;
  }
}
function resolveEdgeOverlaps(positions, edges, minClearance) {
  for (let pass = 0; pass < 4; pass++) {
    let adjusted = false;
    for (const edge of edges) {
      const src = positions.get(edge.source);
      const tgt = positions.get(edge.target);
      if (!src || !tgt) continue;
      const candidate = {
        ax: src[0],
        az: src[2],
        bx: tgt[0],
        bz: tgt[2]
      };
      const others = collectEdgeSegments(positions, edges, edge);
      for (const other of others) {
        if (distSegmentSegment2D(candidate, other) >= minClearance) continue;
        const nudge = (hashSceneId(edge.target, `edge-${pass}`) - 0.5) * COSMOS_BRANCH_SIBLING_SPACING * 0.35;
        positions.set(edge.target, [
          tgt[0] + nudge,
          tgt[1],
          tgt[2] - minClearance * 0.15
        ]);
        adjusted = true;
        break;
      }
    }
    if (!adjusted) break;
  }
}
function layoutFromGraphEdges(ordered, edges, root, anchor) {
  const positions = /* @__PURE__ */ new Map();
  const sceneById = new Map(ordered.map((scene) => [scene.id, scene]));
  const orderIndex = new Map(ordered.map((scene, idx) => [scene.id, idx]));
  const nodeCount = ordered.length;
  positions.set(root.id, [anchor[0], anchor[1], anchor[2]]);
  const childrenByParent = buildChildrenByParent(edges);
  for (const kids of childrenByParent.values()) {
    kids.sort(
      (a, b) => (orderIndex.get(a) ?? 0) - (orderIndex.get(b) ?? 0)
    );
  }
  const visited = /* @__PURE__ */ new Set([root.id]);
  const queue = [root.id];
  while (queue.length > 0) {
    const parentId = queue.shift();
    const parentPos = positions.get(parentId);
    if (!parentPos) continue;
    const kids = (childrenByParent.get(parentId) ?? []).filter(
      (childId) => sceneById.has(childId)
    );
    kids.forEach((childId, idx) => {
      if (visited.has(childId)) return;
      let candidate = placeChildScene(
        parentPos,
        parentId,
        childId,
        idx,
        kids.length,
        nodeCount
      );
      for (let attempt = 0; attempt < 10; attempt++) {
        const attemptAngle = hashSceneId(childId, `try-${attempt}`) * Math.PI * 2;
        if (attempt > 0) {
          const reach = COSMOS_BRANCH_SIBLING_SPACING * (0.6 + attempt * 0.12);
          candidate = [
            parentPos[0] + Math.cos(attemptAngle) * reach,
            parentPos[1] + sceneLevelJitter(childId),
            parentPos[2] - COSMOS_SCENE_LEVEL_SPACING * (0.85 + hashSceneId(childId, `d-${attempt}`) * 0.25)
          ];
        }
        const trial = new Map(positions);
        trial.set(childId, candidate);
        if (nodeClearanceOk(positions, childId, candidate, COSMOS_MIN_NODE_CLEARANCE) && edgeClearanceOk(
          trial,
          edges,
          parentId,
          childId,
          COSMOS_MIN_EDGE_CLEARANCE
        )) {
          break;
        }
      }
      positions.set(childId, candidate);
      visited.add(childId);
      queue.push(childId);
    });
  }
  let spineLevel = 0;
  for (const scene of ordered) {
    if (positions.has(scene.id)) continue;
    spineLevel += 1;
    const angle = spineLevel * GOLDEN_ANGLE + hashSceneId(scene.id) * 0.55;
    const reach = COSMOS_BRANCH_SIBLING_SPACING * (0.8 + hashSceneId(scene.id, "spine") * 0.7);
    positions.set(scene.id, [
      anchor[0] + Math.cos(angle) * reach,
      anchor[1] + sceneLevelJitter(scene.id),
      anchor[2] - spineLevel * COSMOS_SCENE_LEVEL_SPACING
    ]);
  }
  resolveNodeOverlaps(
    positions,
    COSMOS_MIN_NODE_CLEARANCE,
    /* @__PURE__ */ new Set([root.id])
  );
  resolveEdgeOverlaps(positions, edges, COSMOS_MIN_EDGE_CLEARANCE);
  return positions;
}
function computeConstellationScenePositions(scenes, graph, anchor = [0, 0, 0], tier) {
  const positions = /* @__PURE__ */ new Map();
  const ordered = orderScenesForConstellationLayout(scenes, graph);
  if (ordered.length === 0) return positions;
  const root = ordered.find((scene) => scene.is_title) ?? ordered[0];
  const edges = graph?.edges ?? [];
  if (edges.length > 0) {
    const graphPositions = layoutFromGraphEdges(ordered, edges, root, anchor);
    if (tier) clampConstellationToTierBounds(graphPositions, tier);
    return graphPositions;
  }
  positions.set(root.id, [anchor[0], anchor[1], anchor[2]]);
  const nodeCount = ordered.length;
  let spineIndex = 0;
  for (const scene of ordered) {
    if (scene.id === root.id) continue;
    const angle = spineIndex * GOLDEN_ANGLE + hashSceneId(scene.id) * 0.55;
    const reach = COSMOS_BRANCH_SIBLING_SPACING * (0.85 + hashSceneId(scene.id, "d") * 0.75 + Math.min(nodeCount, 8) * 0.05);
    const depthStep = COSMOS_SCENE_LEVEL_SPACING * (0.85 + hashSceneId(scene.id, "depth") * 0.25);
    positions.set(scene.id, [
      anchor[0] + Math.cos(angle) * reach,
      anchor[1] + sceneLevelJitter(scene.id),
      anchor[2] - (spineIndex + 1) * depthStep
    ]);
    spineIndex += 1;
  }
  for (let i = 1; i < ordered.length; i++) {
    clampEdgeLength(
      positions,
      ordered[i - 1].id,
      ordered[i].id,
      COSMOS_MAX_SCENE_EDGE_LENGTH
    );
  }
  resolveNodeOverlaps(
    positions,
    COSMOS_MIN_NODE_CLEARANCE,
    /* @__PURE__ */ new Set([root.id])
  );
  if (tier) clampConstellationToTierBounds(positions, tier);
  return positions;
}
function constellationFootprintRadius(localPositions, padding = COSMOS_CONSTELLATION_VISUAL_PADDING) {
  let maxReach = COSMOS_DEFAULT_FOOTPRINT_RADIUS;
  for (const [x, , z] of localPositions.values()) {
    maxReach = Math.max(maxReach, Math.hypot(x, z) + padding);
  }
  return maxReach;
}
var TITLE_ARC_WORLD_SCALE = 58;
function estimateConstellationTitleFootprintRadius(title, sceneFootprint = COSMOS_DEFAULT_FOOTPRINT_RADIUS) {
  const trimmed = title.trim();
  if (!trimmed) return sceneFootprint;
  const fontSize = 34;
  const letterSpacing = 6;
  const glyphWidth = fontSize * 0.62;
  const chars = trimmed.length;
  const pathWidthSvg = chars * glyphWidth + Math.max(0, chars - 1) * letterSpacing + 56;
  const titleRadius = pathWidthSvg / TITLE_ARC_WORLD_SCALE / 2 + COSMOS_CONSTELLATION_VISUAL_PADDING;
  return Math.max(sceneFootprint, titleRadius);
}
function circlesSeparated(ax, az, aRadius, bx, bz, bRadius, minGap) {
  return Math.hypot(ax - bx, az - bz) >= aRadius + bRadius + minGap;
}
function findConstellationAnchor(footprintRadius, placed, minGap, maxReach) {
  if (placed.length === 0) return [0, 0, 0];
  let angle = 0;
  let radius = 0;
  const angleStep = 0.62;
  const radiusGrowth = minGap * 0.75;
  for (let attempt = 0; attempt < 8e3; attempt++) {
    angle += angleStep;
    radius += radiusGrowth / (2 * Math.PI);
    const cx = Math.cos(angle) * radius;
    const cz = Math.sin(angle) * radius;
    const withinTier = maxReach == null || Math.hypot(cx, cz) + footprintRadius <= maxReach;
    const ok = withinTier && placed.every(
      (other) => circlesSeparated(cx, cz, footprintRadius, other.x, other.z, other.radius, minGap)
    );
    if (ok) return [cx, 0, cz];
  }
  const fallback = Math.min(
    placed.length * (minGap + footprintRadius * 2),
    maxReach != null ? Math.max(0, maxReach - footprintRadius) : placed.length * (minGap + footprintRadius * 2)
  );
  return [fallback, 0, 0];
}
function distributeConstellationAnchors(items, options = {}) {
  const minGapScenes = options.minGapScenes ?? COSMOS_CONSTELLATION_GAP_SCENES;
  const levelSpacing = options.levelSpacing ?? COSMOS_SCENE_LEVEL_SPACING;
  const minGap = minGapScenes * levelSpacing;
  const maxReach = options.maxAnchorReach;
  const anchors = /* @__PURE__ */ new Map();
  const placed = [];
  for (const item of items) {
    const [x, , z] = findConstellationAnchor(
      item.footprintRadius,
      placed,
      minGap,
      maxReach
    );
    anchors.set(item.id, [x, 0, z]);
    placed.push({ x, z, radius: item.footprintRadius });
  }
  return anchors;
}
var TIER_NEBULA_RADIUS = 38;
var CLUSTER_SEPARATION = 64;
var TIER_OFFSETS = {
  private: [-CLUSTER_SEPARATION, 0, 0],
  limited: [0, 0, 0],
  worldwide: [CLUSTER_SEPARATION, 0, 0]
};
function clampPositionToTierNebula(pos, tier) {
  const center = TIER_OFFSETS[tier];
  const dx = pos[0] - center[0];
  const dy = pos[1] - center[1];
  const dz = pos[2] - center[2];
  const dist = Math.hypot(dx, dy, dz);
  if (dist <= TIER_NEBULA_RADIUS) return pos;
  const scale = TIER_NEBULA_RADIUS / dist;
  return [
    center[0] + dx * scale,
    center[1] + dy * scale,
    center[2] + dz * scale
  ];
}
function clampConstellationToTierBounds(positions, tier) {
  const tierCenter = TIER_OFFSETS[tier];
  let maxDist = 0;
  for (const pos of positions.values()) {
    maxDist = Math.max(
      maxDist,
      Math.hypot(
        pos[0] - tierCenter[0],
        pos[1] - tierCenter[1],
        pos[2] - tierCenter[2]
      )
    );
  }
  if (maxDist <= TIER_NEBULA_RADIUS || maxDist === 0) return;
  const scale = (TIER_NEBULA_RADIUS - 0.5) / maxDist;
  for (const [id, pos] of positions) {
    positions.set(id, [
      tierCenter[0] + (pos[0] - tierCenter[0]) * scale,
      tierCenter[1] + (pos[1] - tierCenter[1]) * scale,
      tierCenter[2] + (pos[2] - tierCenter[2]) * scale
    ]);
  }
}
function placeTierCluster(items, tier, options) {
  const tierItems = items.filter((item) => item.tier === tier);
  const offset = TIER_OFFSETS[tier];
  const localAnchors = distributeConstellationAnchors(tierItems, {
    ...options,
    maxAnchorReach: TIER_NEBULA_RADIUS - 2
  });
  const anchors = /* @__PURE__ */ new Map();
  for (const [id, pos] of localAnchors) {
    const shifted = [pos[0] + offset[0], pos[1], pos[2] + offset[2]];
    anchors.set(id, shifted);
  }
  return {
    anchors,
    center: offset,
    radius: TIER_NEBULA_RADIUS
  };
}
function distributeClusteredConstellationAnchors(items, options = {}) {
  const privateCluster = placeTierCluster(items, "private", options);
  const limitedCluster = placeTierCluster(items, "limited", options);
  const worldwideCluster = placeTierCluster(items, "worldwide", options);
  const anchors = new Map([
    ...privateCluster.anchors,
    ...limitedCluster.anchors,
    ...worldwideCluster.anchors
  ]);
  return {
    anchors,
    clusters: {
      private: { center: privateCluster.center, radius: privateCluster.radius },
      limited: { center: limitedCluster.center, radius: limitedCluster.radius },
      worldwide: { center: worldwideCluster.center, radius: worldwideCluster.radius }
    }
  };
}
function CosmosSandbox({
  films,
  onFilmSelect,
  seed = 1
}) {
  const placed = useMemo(() => {
    const positions = distributeStars(films.length, { seed });
    return films.map((film, i) => ({
      film,
      visual: { ...cosmosMapping(film), position: positions[i] }
    }));
  }, [films, seed]);
  return /* @__PURE__ */ jsx(World, { children: placed.map(({ film, visual }) => /* @__PURE__ */ jsx(
    Star,
    {
      visual,
      onSelect: onFilmSelect ? () => onFilmSelect(film) : void 0
    },
    film.id
  )) });
}
function ClusterNebula({
  center,
  radius,
  color,
  opacity = 0.12,
  particleCount = 300,
  highlighted = false
}) {
  const pointsRef = useRef(null);
  const dustRef = useRef(null);
  const resolvedOpacity = highlighted ? Math.min(opacity * 2.6, 0.32) : opacity;
  const resolvedCount = highlighted ? Math.round(particleCount * 1.35) : particleCount;
  const dustCount = highlighted ? 36 : 22;
  const { positions, sizes } = useMemo(() => {
    const pos = new Float32Array(resolvedCount * 3);
    const sz = new Float32Array(resolvedCount);
    for (let i = 0; i < resolvedCount; i++) {
      const r = Math.pow(Math.random(), 0.6) * radius;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.4;
      pos[i * 3 + 2] = r * Math.cos(phi);
      sz[i] = 0.4 + Math.random() * 1.2;
    }
    return { positions: pos, sizes: sz };
  }, [resolvedCount, radius]);
  const dustMotes = useMemo(() => {
    const items = [];
    for (let i = 0; i < dustCount; i++) {
      items.push({
        angle: i / dustCount * Math.PI * 2,
        orbit: 0.55 + i % 5 * 0.09,
        yOffset: (i % 7 - 3) * radius * 0.04,
        phase: i * 0.71 % (Math.PI * 2),
        size: 0.05 + i % 4 * 0.025,
        drift: 0.45 + i % 4 * 0.18
      });
    }
    return items;
  }, [dustCount, radius]);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    if (pointsRef.current) {
      const spin = highlighted ? 0.018 : 0.011;
      pointsRef.current.rotation.y = t * spin;
      pointsRef.current.rotation.x = Math.sin(t * 3e-3) * 0.03;
    }
    if (dustRef.current) {
      const speed = highlighted ? 0.55 : 0.38;
      dustRef.current.children.forEach((child, i) => {
        const mote = dustMotes[i];
        if (!mote) return;
        const a = mote.angle + t * speed * mote.drift + mote.phase;
        child.position.set(
          Math.cos(a) * radius * mote.orbit,
          mote.yOffset + Math.sin(t * 1.2 + mote.phase) * radius * 0.06,
          Math.sin(a) * radius * mote.orbit
        );
      });
    }
  });
  const texture = useMemo(() => {
    const canvas = document.createElement("canvas");
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext("2d");
    const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    gradient.addColorStop(0, "rgba(255,255,255,1)");
    gradient.addColorStop(0.3, "rgba(255,255,255,0.6)");
    gradient.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 64, 64);
    const tex = new THREE.CanvasTexture(canvas);
    tex.needsUpdate = true;
    return tex;
  }, []);
  const dustOpacity = highlighted ? 0.55 : 0.38;
  return /* @__PURE__ */ jsxs("group", { position: center, children: [
    /* @__PURE__ */ jsxs("points", { ref: pointsRef, children: [
      /* @__PURE__ */ jsxs("bufferGeometry", { children: [
        /* @__PURE__ */ jsx(
          "bufferAttribute",
          {
            attach: "attributes-position",
            array: positions,
            count: resolvedCount,
            itemSize: 3
          }
        ),
        /* @__PURE__ */ jsx(
          "bufferAttribute",
          {
            attach: "attributes-size",
            array: sizes,
            count: resolvedCount,
            itemSize: 1
          }
        )
      ] }),
      /* @__PURE__ */ jsx(
        "pointsMaterial",
        {
          color,
          size: highlighted ? 1.65 : 1.35,
          transparent: true,
          opacity: resolvedOpacity,
          map: texture,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          sizeAttenuation: true
        }
      )
    ] }),
    /* @__PURE__ */ jsx("group", { ref: dustRef, children: dustMotes.map((mote, i) => /* @__PURE__ */ jsxs("mesh", { children: [
      /* @__PURE__ */ jsx("sphereGeometry", { args: [mote.size, 6, 6] }),
      /* @__PURE__ */ jsx(
        "meshBasicMaterial",
        {
          color,
          transparent: true,
          opacity: dustOpacity,
          toneMapped: false,
          depthWrite: false,
          blending: THREE.AdditiveBlending
        }
      )
    ] }, i)) })
  ] });
}
function ClusterLabel({
  position,
  label,
  color = "#8ec8f8",
  highlighted = false,
  offsetY = 12,
  hidden = false
}) {
  if (hidden || !label.trim()) return null;
  const labelY = position[1] + offsetY;
  return /* @__PURE__ */ jsx(Billboard, { position: [position[0], labelY, position[2]], follow: true, children: /* @__PURE__ */ jsx(
    Html,
    {
      center: true,
      zIndexRange: [12, 0],
      style: { pointerEvents: "none", userSelect: "none" },
      children: /* @__PURE__ */ jsx(
        "div",
        {
          style: {
            display: "inline-block",
            width: "max-content",
            maxWidth: "min(90vw, 420px)",
            fontFamily: "'Inter', sans-serif",
            fontSize: highlighted ? "20px" : "16px",
            fontWeight: highlighted ? 700 : 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color,
            padding: highlighted ? "8px 18px" : "6px 14px",
            borderRadius: 8,
            background: "rgba(4,10,20,0.9)",
            border: highlighted ? `1px solid ${color}99` : `1px solid ${color}44`,
            boxShadow: highlighted ? `0 0 24px ${color}55, 0 4px 20px rgba(0,0,0,0.85)` : "0 2px 16px rgba(0,0,0,0.8)",
            filter: highlighted ? "brightness(1.2) saturate(1.25)" : void 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            textAlign: "center"
          },
          children: label
        }
      )
    }
  ) });
}

export { COSMOS_BRANCH_SIBLING_SPACING, COSMOS_CONSTELLATION_GAP_SCENES, COSMOS_CONSTELLATION_VISUAL_PADDING, COSMOS_DEFAULT_FOOTPRINT_RADIUS, COSMOS_MAX_SCENE_EDGE_LENGTH, COSMOS_MIN_EDGE_CLEARANCE, COSMOS_MIN_NODE_CLEARANCE, COSMOS_SCENE_BRANCH_SPACING, COSMOS_SCENE_LEVEL_SPACING, ClusterLabel, ClusterNebula, Constellation, ConstellationEdge, ConstellationStoryTitleInline, ConstellationTitle, CosmosSandbox, DEFAULT_CONSTELLATION_APPEARANCE, DraggableAtomGroup, PublishedConstellationAura, Star, StarBillboard, TIER_NEBULA_RADIUS, World, clampPositionToTierNebula, computeConstellationBounds, computeConstellationScenePositions, constellationFootprintRadius, distributeClusteredConstellationAnchors, distributeConstellationAnchors, distributeStars, estimateConstellationTitleFootprintRadius, mergeConstellationAppearance };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map